import React, { useState, useEffect } from "react";
import ScaledStage from "./ScaledStage";
import { OPPORTUNITY_CARDS } from "../data/content";

const Card = ({ c, style, className = "" }) => (
  <div
    className={`flex flex-col items-center justify-start gap-1 rounded-[16px] border border-[#F1F5F9] bg-[#F6F6F6] p-5 shadow-[2px_2px_10px_rgba(0,0,0,0.10)] ${className}`}
    style={style}
  >
    <img src={`/images/${c.img}.png`} alt={c.title} className="h-[72px] w-auto object-contain" />
    <div className="flex flex-col items-center">
      <span className="text-center text-[14px] font-bold leading-5 text-[#0F172A]">{c.title}</span>
      <span className="px-1 text-center text-[12px] font-normal leading-4 text-[#64748B]">{c.sub}</span>
    </div>
  </div>
);

const AiOrb = ({ style }) => (
  <div className="absolute" style={style}>
    <div className="absolute animate-pulse" style={{ width: 161, height: 161, left: -36, top: -36, opacity: 0.2, borderRadius: 9999, filter: "blur(3px)", background: "linear-gradient(45deg, #22D3EE 0%, #2563EB 100%)" }} />
    <div className="absolute" style={{ width: 132, height: 132, left: -21, top: -21, opacity: 0.5, borderRadius: 9999, filter: "blur(1px)", background: "linear-gradient(45deg, #22D3EE 0%, #2563EB 100%)" }} />
    <div
      className="relative flex items-center justify-center animate-[floaty_5s_ease-in-out_infinite]"
      style={{ width: 90, height: 90, borderRadius: 9999, background: "linear-gradient(45deg, #22D3EE 0%, #2563EB 100%)", boxShadow: "0 16px 24px -5px rgba(6,182,212,0.35)" }}
    >
      <span className="text-[26px] font-bold text-white">AI</span>
    </div>
  </div>
);

const OpportunitiesSection = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((prev) => prev + 1);
    }, 2200); 
    return () => clearInterval(timer);
  }, []);

  const TOTAL_CARDS = OPPORTUNITY_CARDS.length;
  const ANGLE_STEP = 360 / TOTAL_CARDS;
  
  // Independent radiuses create the oval shape
  const RADIUS_X = 350; // Wider horizontal spread
  const RADIUS_Y = 210; // Tighter vertical spread to fit within the 640px height

  return (
    <section id="opportunities" className="w-full overflow-hidden" style={{ background: "#F1F2F3" }}>
      
      <div className="mx-auto hidden max-w-[1440px] lg:block">
        {/* Reverted back to the original 640 height */}
        <ScaledStage width={1440} height={640}>
          
          <div className="absolute" style={{ left: 70, top: 160, width: 380 }}>
            <div className="flex flex-col gap-6">
              <p className="text-[13px] font-bold uppercase leading-[18px] tracking-[2.2px] text-[#64748B]">FIND YOUR STARTING POINT</p>
              <h2 className="text-[44px] font-bold leading-[48px] text-[#0F172A]">But where should AI begin in yours?</h2>
              <p className="w-full text-[18px] font-normal leading-[30px] text-[#475569]">
                Every business has unique opportunities. Explore how AI could create value across your business.
              </p>
            </div>
          </div>
          
          {/* Reverted center coordinates */}
          <AiOrb style={{ left: 875, top: 275 }} />

          <div className="absolute pointer-events-none" style={{ left: 920, top: 320 }}>
            
            {OPPORTUNITY_CARDS.map((c, i) => {
              const currentAngle = (i * ANGLE_STEP) + (step * ANGLE_STEP);
              
              // Convert degrees to radians (subtract 90 so the first item starts at the very top)
              const radians = (currentAngle - 90) * (Math.PI / 180);
              
              // Calculate exact X and Y coordinates along the elliptical path
              const x = RADIUS_X * Math.cos(radians);
              const y = RADIUS_Y * Math.sin(radians);

              return (
                <div
                  key={c.title}
                  className="absolute pointer-events-auto"
                  style={{
                    left: 0,
                    top: 0,
                    // Use standard translate targeting the calculated coordinates
                    transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                    transition: "transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    width: 176,
                    height: 158
                  }}
                >
                  <Card c={c} className="h-full w-full" />
                </div>
              );
            })}
          </div>
          
        </ScaledStage>
      </div>

      <div className="px-6 py-16 lg:hidden">
        <div className="flex flex-col gap-5">
          <p className="text-[12px] font-bold uppercase tracking-[2.2px] text-[#64748B]">FIND YOUR STARTING POINT</p>
          <h2 className="text-[32px] font-bold leading-[38px] text-[#0F172A]">But where should AI begin in yours?</h2>
          <p className="text-[17px] leading-[28px] text-[#475569]">Every business has unique opportunities. Explore how AI could create value across your business.</p>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {OPPORTUNITY_CARDS.map((c) => (
            <Card key={c.title} c={c} className="h-full w-full" />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpportunitiesSection;